import { assertAPIResponse } from "../services/api-response";
import axiosInstance from "../services/axiosInstance";
import { axiosWithAbort } from "../services/axiosWithAbort";
import handleAPIError, {
  MESSAGE_CODES,
  showSuccessToast,
} from "../services/toast";

export const organizationalStructureAPI = {
  async updateOperatingStatus(companyId, body) {
    const response = await axiosInstance.put(
      `/org/company/${encodeURIComponent(companyId)}/operating_status`, body,
    );
    if (response.data?.success !== true) {
      throw new Error(response.data?.message || "Unable to update operating status");
    }
    return response.data.data;
  },

  //取得company list
  async getCompanyListAPI(userId) {
    try {
      const response = await axiosWithAbort("companyList", {
        method: "get",
        url: `/org/company/${userId}`,
      });

      return response?.data?.data;
    } catch (error) {
      console.error("Error fetching company list data:", error);
    }
  },

  //////////  取得下拉式選單
  async getCountryOptionAPI() {
    try {
      const response = await axiosInstance.get(`/org/company/country_list`);
      return response.data.data; // 根據回傳結構，也許你需要改成 response.data.data
    } catch (error) {
      handleAPIError(error);
      console.error("Error fetching country list:", error);
      return null;
    }
  },

  //////////  取得上層層級選項
  async getUpperLevelList(rootId) {
    try {
      const response = await axiosInstance.get(
        `/org/company/${rootId}/parent_dropdown_list`,
      );
      return response.data.data; // 根據回傳結構，也許你需要改成 response.data.data
    } catch (error) {
      handleAPIError(error);
      console.error("Error fetching uppper level options:", error);
      return null;
    }
  },

  //////////修改公司info
  async editCompanyInfoAPI(params) {
    const requestBody = {
      companyName: params.name,
      address: params.address,
      country: params.country,
    };
    // 只有當 type 和 parentId 存在時才加入
    if (params.level) {
      requestBody.type = params.level;
    }
    if (params.upperLevel) {
      requestBody.parentId = params.upperLevel;
    }
    try {
      const response = await axiosInstance.put(
        `/org/company/${params.id}/update`,
        requestBody,
      );
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error("Error editing company info:", error);
      return null; // 失敗時回傳 null
    }
  },

  //取得組織架構list
  async getOrganizationListAPI(org_id) {
    try {
      const response = await axiosInstance.get(
        `/org/organization/${org_id}/descendants`,
      );
      return response.data.data; // 返回 API 的資料
    } catch (error) {
      handleAPIError(error);
      console.error("Error fetching company list data:", error);
      return []; // 失敗時回傳 null
    }
  },

  //刪除分類
  async deleteCategoryAPI(org_id) {
    try {
      const response = await axiosInstance.delete(
        `/org/organization/${org_id}/del`,
      );
      return response.data.data; // 返回 API 的資料
    } catch (error) {
      handleAPIError(error);
      console.error("Error fetching company list data:", error);
      return []; // 失敗時回傳 null
    }
  },

  //刪除角色
  async deleteUserAPI(user_id) {
    try {
      const response = await axiosInstance.delete(`/org/orguser/${user_id}`);
      showSuccessToast({
        apiData: response.data,
        scenarioCode: MESSAGE_CODES.ORG_DELETE_USER_SUCCESS,
      });
      return response.data.data; // 返回 API 的資料
    } catch (error) {
      handleAPIError(error);
      console.error("Error delete user data:", error);
      return []; // 失敗時回傳 null
    }
  },

  //////////修改層級
  async editNodeDepthAPI(id, parentId) {
    const requestBody = {
      new_parentId: parentId,
    };
    try {
      const response = await axiosInstance.put(
        `/org/organization/${id}/move`,
        requestBody,
      );
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error("Error editing node depth:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////////修改層級
  async editNodeNameAPI(id, name) {
    try {
      const response = await axiosInstance.put(
        `/org/organization/${id}/update?new_name=${encodeURIComponent(name)}`,
      );
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error("Error editing node name:", error);
      return null; // 失敗時回傳 null
    }
  },

  //////新增category
  async addCategoryAPI(name, parentId) {
    const requestBody = {
      orgName: name,
      orgType: "CATEGORY",
      parentId: parentId,
    };
    try {
      const response = await axiosInstance.post(
        `/org/organization/`,
        requestBody,
      );
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error("Error adding category:", error);
      return null; // 失敗時回傳 null
    }
  },

  //OrgUser
  async getOrgUserListAPI(userId) {
    try {
      const response = await axiosInstance.get(
        `/org/orguser/user_list/${userId}`,
      );
      return response.data.data; // 根據回傳結構，也許你需要改成 response.data.data
    } catch (error) {
      handleAPIError(error);
      console.error("Error fetching org user list:", error);
      return null;
    }
  },

  //取得角色名稱單位下拉式選單
  async getRoleDropdownListAPI() {
    try {
      const response = await axiosInstance.get(
        `/org/orguser/role_dropdown_list`,
      );
      return response.data.data; // 根據回傳結構，也許你需要改成 response.data.data
    } catch (error) {
      handleAPIError(error);
      console.error("Error fetching role dropdown list:", error);
      return null;
    }
  },

  //取得公司名稱下拉式選單
  async getCompanyDropdownListAPI(id) {
    try {
      const response = await axiosInstance.get(
        `/org/orguser/company_dropdown_list/${id}`,
      );
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
      console.error("Error fetching company dropdown list:", error);
      return null;
    }
  },

  //add org user
  async addOrgUserAPI(
    roleId,
    accountName,
    email,
    password,
    company, //authId
    rootId,
    canEdit,
    type, // 代表 scopeType ("AUTH" 或 "COMPANY")
    companyIds,
  ) {
    const requestBody = {
      roleId: roleId,
      userName: accountName,
      email: email,
      password: password,
      rootId: rootId,
      isEdit: canEdit,
      type: type,
    };

    if (type === "AUTH") {
      requestBody.authId = company;
    } else if (type === "COMPANY") {
      requestBody.companyIds = companyIds;
    }

    try {
      const response = await axiosInstance.post(`/org/orguser/`, requestBody);
      const resData = response.data;

      if (resData?.success !== true) {
        handleAPIError(null, {
          apiData: resData
        });
      } else {
        showSuccessToast({
          apiData: response.data,
          scenarioCode: MESSAGE_CODES.ORG_CREATE_USER_SUCCESS,
        });
      }

      return resData;
    } catch (error) {
      handleAPIError(error);
      console.error("Error adding org user:", error);
    }
  },

  //edit
  async editOrgUserAPI(
    userId,
    roleId,
    accountName,
    email,
    company, // 代表 authId
    canEdit,
    type,
    companyIds, // 代表 scopeType ("AUTH" 或 "COMPANY")
  ) {
    const requestBody = {
      roleId: roleId,
      userName: accountName,
      email: email,
      type: type,
      isEdit: canEdit,
    };

    if (type === "AUTH") {
      requestBody.authId = company;
    } else if (type === "COMPANY") {
      requestBody.companyIds = companyIds;
    }
    try {
      const response = await axiosInstance.put(
        `/org/orguser/super_admin/account/${userId}`,
        requestBody,
      );
      if (response.data?.success !== true) {
        handleAPIError(null, {
          apiData: response.data
        });
      } else {
        showSuccessToast({
          apiData: response.data,
          scenarioCode: MESSAGE_CODES.ORG_UPDATE_USER_SUCCESS,
        });
      }
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error("Error adding org user:", error);
      return null; // 失敗時回傳 null
    }
  },

  //edit super admin
  async editSuperAdminAPI(
    userId,
    accountName,
    email,
    companyName,
    authId,
    type,
  ) {
    const requestBody = {
      userName: accountName,
      email: email,
      newName: companyName,
      type: type,
    };
    if (type === "AUTH") {
      requestBody.authId = authId;
    }
    try {
      const response = await axiosInstance.put(
        `/org/orguser/super_admin/${userId}`,
        requestBody,
      );
      showSuccessToast({
        apiData: response.data,
        scenarioCode: MESSAGE_CODES.ORG_UPDATE_USER_SUCCESS,
      });
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error("Error adding org user:", error);
      return null; // 失敗時回傳 null
    }
  },

  //改變密碼
  async changePasswordAPI(userId, currentPassword, newPassword) {
    const requestBody = {
      currentPassword: currentPassword,
      newPassword: newPassword,
    };
    try {
      const response = await axiosInstance.put(
        `/user/accountuser/${userId}/password`,
        requestBody,
      );
      const result = response.data;
      // 檢查 API 回傳的 success 狀態
      if (result.success) {
        showSuccessToast({
          apiData: result,
          scenarioCode: MESSAGE_CODES.PASSWORD_UPDATE_SUCCESS,
        });
        return result;
      } else {
        // API 回傳 success: false 的情況
        const errorMessage = result.error || "Failed to change password";
        throw new Error(errorMessage);
      }
    } catch (error) {
      // 網路錯誤或其他例外狀況
      handleAPIError(error);
      console.error("Error changing password:", error);
      return null;
    }
  },

  //改變其他人的密碼
  async changeOthersPasswordAPI(userId, newPassword) {
    const requestBody = {
      newPassword: newPassword,
    };
    try {
      const response = await axiosInstance.put(
        `/user/accountuser/super_admin/${userId}/password`,
        requestBody,
      );
      const result = response.data;
      // 檢查 API 回傳的 success 狀態
      if (result.success) {
        showSuccessToast({
          apiData: result,
          scenarioCode: MESSAGE_CODES.PASSWORD_UPDATE_SUCCESS,
        });
        return result;
      } else {
        // API 回傳 success: false 的情況
        const errorMessage = result.error || "Failed to change password";
        throw new Error(errorMessage);
      }
    } catch (error) {
      // 網路錯誤或其他例外狀況
      handleAPIError(error);
      console.error("Error changing password:", error);
      return null;
    }
  },

  //重置所有公司設定
  async resetCompanyAPI(rootId) {
    const requestBody = {
      rootId: rootId,
    };
    try {
      const response = await axiosInstance.post(
        `/org/company/reset_company_tree`,
        requestBody,
      );
      showSuccessToast({
        apiData: response.data,
        scenarioCode: MESSAGE_CODES.ORG_RESET_SUCCESS,
      });
      return response.data.data; // 返回 API 的資料
    } catch (error) {
      handleAPIError(error);
      console.error("Error reset all company:", error);
      return []; // 失敗時回傳 null
    }
  },

  //匯出使用者至excel
  async downloadUserListExcel(rootId, lang) {
    const params = {
      root_company: rootId,
      language: lang,
    };

    try {
      const response = await axiosInstance.post(
        `/org/orguser/export_user`,
        {},
        {
          params: params,
          responseType: "blob",
        },
      );
      // 創建 Blob 物件
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // 創建下載連結
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"];
      let fileName = "download.xlsx"; // 預設檔名
      if (contentDisposition) {
        const filenameRegex = /filename\*=UTF-8''([^;]+)/;
        const matches = filenameRegex.exec(contentDisposition);

        if (matches && matches[1]) {
          fileName = decodeURIComponent(matches[1]);
        } else {
          // 如果沒有 UTF-8 編碼,嘗試普通 filename
          const fallbackRegex = /filename="?([^";\n]+)"?/;
          const fallbackMatches = fallbackRegex.exec(contentDisposition);
          if (fallbackMatches && fallbackMatches[1]) {
            // 嘗試解碼可能的 URI 編碼
            try {
              fileName = decodeURIComponent(fallbackMatches[1]);
            } catch (e) {
              fileName = fallbackMatches[1];
            }
          }
        }
      }

      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click(); // 自動點擊下載
      document.body.removeChild(link); // 下載後移除連結
    } catch (error) {
      handleAPIError(error);
      console.error("Error downloading user list excel:", error);
      return null;
    }
  },

  //批量使用excel建立使用者
  async uploadUserListExcel(rootId, file) {
    const formData = new FormData();
    formData.append("file", file);

    const params = {
      rootId: rootId,
    };

    try {
      const response = await axiosInstance.post(
        `/org/orguser/batch_create_user`,
        formData,
        {
          params: params,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      assertAPIResponse(response);
      showSuccessToast({
        apiData: response.data,
        scenarioCode: MESSAGE_CODES.FILE_UPLOAD_SUCCESS,
      });
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error("Error uploading user list excel:", error);
      return null;
    }
  },

  //更改使用者架構且會將所有user刪除
  async setUserStructureMode(type, rootId) {
    try {
      const response = await axiosInstance.put(
        `/org/orguser/change/${type}/${rootId}`,
        {},
      );
      showSuccessToast({
        apiData: response.data,
        scenarioCode: MESSAGE_CODES.ORG_SWITCH_STRUCTURE_SUCCESS,
      });
      return response.data;
    } catch (error) {
      handleAPIError(error);
      console.error("Error changing user structure:", error);
      return null;
    }
  },
};
